/**
 * Write-ahead queue for offline mutations.
 *
 * Page-side (this file) owns the IDB stores `wal-queue` and `wal-blobs`.
 * The service worker forwards intercepted non-GET requests here via
 * postMessage (see public/sw.js), and this module also handles direct
 * enqueue from application code that wants fire-and-forget semantics.
 *
 * Drain policy:
 *  - At most 1 in-flight fetch.
 *  - Success: delete row + body blob.
 *  - 4xx (non-429): drop row (server rejected, retrying won't help).
 *  - 5xx / network / 429: increment attempts + store lastError, keep row.
 */

import {
  STORE_WAL_QUEUE,
  STORE_WAL_BLOBS,
  openDb,
  get,
  put,
  del,
  getAll,
  count,
} from './idb';
import type { WalQueueRow } from './idb';

const CHANNEL_NAME = 'wal';

function makeBlobKey(): string {
  // crypto.randomUUID is available in all supported targets (ES2022 + modern browsers).
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `wal-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function broadcastQueueLength(n: number): void {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const ch = new BroadcastChannel(CHANNEL_NAME);
    ch.postMessage({ type: 'length', length: n });
    ch.close();
  } catch {
    // best-effort
  }
}

async function broadcastCurrentLength(): Promise<void> {
  try {
    const n = await queueLength();
    broadcastQueueLength(n);
  } catch {
    // ignore
  }
}

export async function enqueueMutation(req: Request): Promise<void> {
  // Read body ONCE into a Blob so we can replay later. GET/HEAD have no body.
  let bodyBlobKey: string | undefined;
  if (req.method !== 'GET' && req.method !== 'HEAD') {
    const blob = await req.clone().blob();
    if (blob.size > 0) {
      bodyBlobKey = makeBlobKey();
      await put(STORE_WAL_BLOBS, blob, bodyBlobKey);
    }
  }

  const headers: Record<string, string> = {};
  req.headers.forEach((value, key) => {
    headers[key] = value;
  });

  const row: WalQueueRow = {
    createdAt: Date.now(),
    method: req.method,
    url: req.url,
    bodyBlobKey,
    headers,
    attempts: 0,
  };
  await put(STORE_WAL_QUEUE, row);
  await broadcastCurrentLength();

  // Best-effort Background Sync registration.
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready;
      const syncReg = (reg as ServiceWorkerRegistration & {
        sync?: { register: (tag: string) => Promise<void> };
      }).sync;
      if (syncReg) {
        await syncReg.register('wal-drain');
      }
    } catch {
      // Background Sync is a progressive enhancement.
    }
  }
}

export async function queueLength(): Promise<number> {
  return count(STORE_WAL_QUEUE);
}

let draining = false;

export async function drainQueue(): Promise<{ sent: number; failed: number }> {
  if (draining) return { sent: 0, failed: 0 };
  draining = true;
  let sent = 0;
  let failed = 0;
  try {
    await openDb();
    const rows = (await getAll<WalQueueRow>(STORE_WAL_QUEUE)).sort(
      (a, b) => (a.id ?? 0) - (b.id ?? 0)
    );
    for (const row of rows) {
      if (row.id === undefined) continue;
      let body: Blob | undefined;
      if (row.bodyBlobKey) {
        body = await get<Blob>(STORE_WAL_BLOBS, row.bodyBlobKey);
      }
      let response: Response;
      try {
        response = await fetch(row.url, {
          method: row.method,
          headers: row.headers,
          body: body ?? null,
          credentials: 'include',
        });
      } catch (err) {
        failed += 1;
        const lastError = err instanceof Error ? err.message : String(err);
        await put(STORE_WAL_QUEUE, { ...row, attempts: row.attempts + 1, lastError });
        // Network error: stop draining to preserve insertion order semantics.
        break;
      }

      if (response.ok) {
        await del(STORE_WAL_QUEUE, row.id);
        if (row.bodyBlobKey) await del(STORE_WAL_BLOBS, row.bodyBlobKey);
        sent += 1;
      } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
        // Server rejected definitively — drop row.
        await del(STORE_WAL_QUEUE, row.id);
        if (row.bodyBlobKey) await del(STORE_WAL_BLOBS, row.bodyBlobKey);
        failed += 1;
      } else {
        failed += 1;
        await put(STORE_WAL_QUEUE, {
          ...row,
          attempts: row.attempts + 1,
          lastError: `HTTP ${response.status}`,
        });
        // Transient: stop the drain; caller can retry later.
        break;
      }
    }
  } finally {
    draining = false;
    await broadcastCurrentLength();
  }
  return { sent, failed };
}

export function subscribeQueueLength(cb: (n: number) => void): () => void {
  if (typeof BroadcastChannel === 'undefined') {
    // Emit one initial best-effort read and return a no-op unsubscribe.
    void queueLength().then(cb).catch(() => {});
    return () => {};
  }
  const ch = new BroadcastChannel(CHANNEL_NAME);
  const handler = () => {
    void queueLength().then(cb).catch(() => {});
  };
  ch.addEventListener('message', handler);
  // Fire once with the current value.
  handler();
  return () => {
    ch.removeEventListener('message', handler);
    ch.close();
  };
}
